import type { ComputedRef, Ref } from 'vue';
import { computed, ref, watchEffect } from 'vue';

const JUST_CREATED_STATE_KEY = '__justCreated' as const;

type JustCreatedToken = { key: string };

type HistoryStateRecord = Record<string, unknown>;

const readHistoryState = (): HistoryStateRecord => {
  if (typeof window === 'undefined') {
    return {};
  }
  const state: unknown = window.history.state;
  if (typeof state !== 'object' || state === null) {
    return {};
  }
  return state as HistoryStateRecord;
};

const writeHistoryState = (next: HistoryStateRecord): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.history.replaceState(next, '');
};

const readJustCreatedToken = (): JustCreatedToken | undefined => {
  const token = readHistoryState()[JUST_CREATED_STATE_KEY];
  if (typeof token !== 'object' || token === null) {
    return undefined;
  }
  const candidate = token as Partial<JustCreatedToken>;
  if (typeof candidate.key !== 'string') {
    return undefined;
  }
  return { key: candidate.key };
};

const clearJustCreatedToken = (): void => {
  const next = { ...readHistoryState() };
  delete next[JUST_CREATED_STATE_KEY];
  writeHistoryState(next);
};

/**
 * Build the `state` payload to pass into `router.push` / `router.replace` so
 * the destination's history entry carries a one-shot "just created" marker
 * keyed to `key`. `key` MUST identify the destination entity (e.g. the new
 * ad-group id) so consumers can verify they are the intended target.
 *
 * Router writes `history.state` via `pushState` BEFORE the destination
 * component's setup runs — that is the window `useOnJustCreated` reads in.
 */
export const justCreatedState = (key: string): Record<string, JustCreatedToken> => ({
  [JUST_CREATED_STATE_KEY]: { key },
});

type UseOnJustCreatedParams = {
  isReady: Ref<boolean>;
  /**
   * Entity id this editor instance is for. Must match the marker's key
   * to claim the just-created signal. Accepts a static string or a
   * reactive ref (e.g. `() => route.params.id as string`).
   */
  key: Ref<string | undefined> | (() => string | undefined) | string;
};

type UseOnJustCreatedReturn = {
  isJustCreated: ComputedRef<boolean>;
};

const resolveKey = (key: UseOnJustCreatedParams['key']): string | undefined => {
  if (typeof key === 'string') {
    return key;
  }
  if (typeof key === 'function') {
    return key();
  }
  return key.value;
};

export const useOnJustCreated = ({ isReady, key }: UseOnJustCreatedParams): UseOnJustCreatedReturn => {
  const captured = ref(false);

  watchEffect(() => {
    if (captured.value) {
      return;
    }
    const myKey = resolveKey(key);
    if (myKey === undefined || myKey === '') {
      return;
    }
    const token = readJustCreatedToken();
    if (token === undefined || token.key !== myKey) {
      return;
    }
    captured.value = true;
    clearJustCreatedToken();
  });

  const isJustCreated = computed(() => captured.value && isReady.value);
  return { isJustCreated };
};
